import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useId, useState } from "react";
import { Button } from "../components/ui/button";
import { cn } from "../lib/utils";
import {
  DOMAINE_JEU_DEFAUT,
  ecrireReglagesJeuSchema,
  INDICES_JEU_MAX,
  JAUGE_JEU_MAX,
  JAUGES_JEU_MAX,
  type LivreJeuDetail,
  TITRE_LIVRE_JEU_MAX,
} from "./contract";
import { ALERTE_GRISE, ALERTE_VERTE, BOUTON_CONTOUR, CHAMP } from "./livres-jeux-commun";
import { cleLivre, messageDEchec, RefusJeu, useEcrireReglages } from "./livres-jeux-requetes";
import { titreDeScene } from "./livres-jeux-textes";

/**
 * Les reglages d'un livre (spec du 03/10/2026, D4) : titre, domaine de l'appli, scene de depart,
 * indices et jauges, en formulaire. Le contrat valide avant l'envoi ; la console ecrit a la
 * version lue, et le dit quand quelqu'un a change les reglages entre-temps.
 */

/** `ligne` : la cle React d'une rangee du formulaire, qui ne bouge pas quand on en retire une. */
type Indice = { ligne: number; id: string; libelle: string };
/** Les nombres d'une jauge restent du texte tant qu'on les tape. */
type Jauge = { ligne: number; id: string; libelle: string; max: string; depart: string };

let derniereLigne = 0;
const nouvelleLigne = (): number => {
  derniereLigne += 1;
  return derniereLigne;
};

/** Le premier refus du contrat, en clair : le champ et ce qui ne va pas. */
function refusDuFormulaire(issues: readonly { path: readonly PropertyKey[] }[]): string {
  const chemin = issues[0]?.path ?? [];
  const [champ, rang, sousChamp] = chemin;
  const numero = typeof rang === "number" ? ` ${rang + 1}` : "";
  switch (champ) {
    case "titre":
      return `Le titre doit faire de 1 à ${TITRE_LIVRE_JEU_MAX} caractères : l'appli n'en affiche pas davantage.`;
    case "domaine":
      return `Le domaine doit être un nom de domaine, par exemple ${DOMAINE_JEU_DEFAUT}.`;
    case "indices":
      return sousChamp === "id"
        ? `Indice${numero} : identifiant en minuscules, chiffres et tirets.`
        : `Indice${numero} : un libellé de 1 à 120 caractères.`;
    case "jauges":
      if (sousChamp === "id")
        return `Jauge${numero} : identifiant en minuscules, chiffres et tirets.`;
      if (sousChamp === "libelle") return `Jauge${numero} : un libellé de 1 à 120 caractères.`;
      return `Jauge${numero} : un maximum de 1 à ${JAUGE_JEU_MAX}, et un départ entre 0 et ce maximum.`;
    default:
      return "Un réglage ne passe pas : vérifie chaque champ.";
  }
}

/**
 * Le formulaire se remonte a chaque nouvelle version des reglages (la page le cle sur elle) :
 * il repart toujours de ce que la console a. `enregistre` dit que cette version est la notre.
 */
export function OngletReglages({
  detail,
  enregistre,
  quandEnregistre,
}: {
  detail: LivreJeuDetail;
  enregistre: boolean;
  quandEnregistre: (version: number) => void;
}) {
  const { livre, scenes } = detail;
  const client = useQueryClient();
  const ecrire = useEcrireReglages(livre.id);
  const [titre, setTitre] = useState(livre.titre);
  const [domaine, setDomaine] = useState(livre.domaine);
  const [depart, setDepart] = useState(livre.depart ?? "");
  const [indices, setIndices] = useState<Indice[]>(() =>
    livre.indices.map((indice) => ({ ligne: nouvelleLigne(), ...indice })),
  );
  const [jauges, setJauges] = useState<Jauge[]>(() =>
    livre.jauges.map((jauge) => ({
      ligne: nouvelleLigne(),
      id: jauge.id,
      libelle: jauge.libelle,
      max: String(jauge.max),
      depart: String(jauge.depart),
    })),
  );
  const [refus, setRefus] = useState<string | null>(null);
  const idTitre = useId();
  const idDomaine = useId();
  const idDepart = useId();

  const envoyer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    // Un double appui n'envoie pas deux fois la meme version (le second ferait un faux 409).
    if (ecrire.isPending) return;
    const corps = {
      titre,
      domaine: domaine.trim(),
      depart: depart === "" ? null : depart,
      indices: indices.map((indice) => ({ id: indice.id.trim(), libelle: indice.libelle })),
      jauges: jauges.map((jauge) => ({
        id: jauge.id.trim(),
        libelle: jauge.libelle,
        max: Number(jauge.max),
        depart: Number(jauge.depart),
      })),
      version: livre.reglages_version,
    };
    const lu = ecrireReglagesJeuSchema.safeParse(corps);
    if (!lu.success) {
      setRefus(refusDuFormulaire(lu.error.issues));
      return;
    }
    setRefus(null);
    ecrire.mutate(lu.data, {
      onSuccess: (reponse) => quandEnregistre(reponse.livre.reglages_version),
    });
  };

  const departInconnu = depart !== "" && !scenes.some((scene) => scene.cle === depart);

  return (
    <form className="flex max-w-3xl flex-col gap-5" noValidate onSubmit={envoyer}>
      <section className="grid gap-4 rounded-lg border border-border bg-card p-4 text-card-foreground md:grid-cols-2 md:p-5">
        <div className="flex flex-col gap-1.5 md:col-span-2">
          <label className="text-sm font-semibold" htmlFor={idTitre}>
            Titre
          </label>
          <input
            className={CHAMP}
            id={idTitre}
            maxLength={TITRE_LIVRE_JEU_MAX}
            onChange={(evenement) => setTitre(evenement.target.value)}
            value={titre}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-semibold" htmlFor={idDepart}>
            Scène de départ
          </label>
          <select
            className={CHAMP}
            id={idDepart}
            onChange={(evenement) => setDepart(evenement.target.value)}
            value={depart}
          >
            <option value="">(aucune pour l'instant)</option>
            {departInconnu ? <option value={depart}>{depart} (n'existe plus)</option> : null}
            {[...scenes]
              .sort((a, b) => a.rang - b.rang)
              .map((scene) => (
                <option key={scene.id} value={scene.cle}>
                  {titreDeScene(scene)} ({scene.cle})
                </option>
              ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-semibold" htmlFor={idDomaine}>
            Domaine de l'appli
          </label>
          <input
            aria-describedby={`${idDomaine}-aide`}
            autoCapitalize="none"
            className={cn(CHAMP, "font-mono")}
            id={idDomaine}
            onChange={(evenement) => setDomaine(evenement.target.value)}
            spellCheck={false}
            value={domaine}
          />
          <p className="text-xs text-muted-foreground" id={`${idDomaine}-aide`}>
            Là où mènent les QR codes du livre imprimé.
          </p>
        </div>
      </section>

      <section
        aria-labelledby="livres-jeux-indices"
        className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4 text-card-foreground md:p-5"
      >
        <h2 className="text-base font-semibold" id="livres-jeux-indices">
          Indices
        </h2>
        <p className="text-sm text-muted-foreground">
          Ce que le lecteur trouve en chemin : un choix les gagne, une condition les demande.{" "}
          {INDICES_JEU_MAX} au plus, pour tenir sur la feuille d'aventure.
        </p>
        {indices.map((indice, rang) => (
          <div className="grid gap-2 md:grid-cols-[12rem_minmax(0,1fr)_auto]" key={indice.ligne}>
            <input
              aria-label={`Identifiant de l'indice ${rang + 1}`}
              autoCapitalize="none"
              className={cn(CHAMP, "font-mono")}
              onChange={(evenement) =>
                setIndices((avant) =>
                  avant.map((autre, i) =>
                    i === rang ? { ...autre, id: evenement.target.value } : autre,
                  ),
                )
              }
              placeholder="cle-rouillee"
              spellCheck={false}
              value={indice.id}
            />
            <input
              aria-label={`Libellé de l'indice ${rang + 1}`}
              className={CHAMP}
              onChange={(evenement) =>
                setIndices((avant) =>
                  avant.map((autre, i) =>
                    i === rang ? { ...autre, libelle: evenement.target.value } : autre,
                  ),
                )
              }
              placeholder="La clé rouillée"
              value={indice.libelle}
            />
            <button
              aria-label={`Retirer l'indice ${rang + 1}`}
              className={cn(BOUTON_CONTOUR, "min-h-9 px-3 py-1")}
              onClick={() => setIndices((avant) => avant.filter((_, i) => i !== rang))}
              type="button"
            >
              Retirer
            </button>
          </div>
        ))}
        <div>
          <button
            className={cn(BOUTON_CONTOUR, "min-h-9 px-3 py-1")}
            disabled={indices.length >= INDICES_JEU_MAX}
            onClick={() =>
              setIndices((avant) => [...avant, { ligne: nouvelleLigne(), id: "", libelle: "" }])
            }
            type="button"
          >
            Ajouter un indice
          </button>
        </div>
      </section>

      <section
        aria-labelledby="livres-jeux-jauges"
        className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4 text-card-foreground md:p-5"
      >
        <h2 className="text-base font-semibold" id="livres-jeux-jauges">
          Jauges
        </h2>
        <p className="text-sm text-muted-foreground">
          Ce qui monte et descend (courage, temps) : {JAUGES_JEU_MAX} au plus, chacune de 0 à son
          maximum, {JAUGE_JEU_MAX} au plus : le code de sauvegarde n'en porte pas davantage.
        </p>
        {jauges.map((jauge, rang) => (
          <div
            className="grid grid-cols-2 gap-2 md:grid-cols-[10rem_minmax(0,1fr)_6rem_6rem_auto]"
            key={jauge.ligne}
          >
            <input
              aria-label={`Identifiant de la jauge ${rang + 1}`}
              autoCapitalize="none"
              className={cn(CHAMP, "font-mono")}
              onChange={(evenement) =>
                setJauges((avant) =>
                  avant.map((autre, i) =>
                    i === rang ? { ...autre, id: evenement.target.value } : autre,
                  ),
                )
              }
              placeholder="courage"
              spellCheck={false}
              value={jauge.id}
            />
            <input
              aria-label={`Libellé de la jauge ${rang + 1}`}
              className={CHAMP}
              onChange={(evenement) =>
                setJauges((avant) =>
                  avant.map((autre, i) =>
                    i === rang ? { ...autre, libelle: evenement.target.value } : autre,
                  ),
                )
              }
              placeholder="Courage"
              value={jauge.libelle}
            />
            <input
              aria-label={`Maximum de la jauge ${rang + 1}`}
              className={CHAMP}
              inputMode="numeric"
              onChange={(evenement) =>
                setJauges((avant) =>
                  avant.map((autre, i) =>
                    i === rang ? { ...autre, max: evenement.target.value } : autre,
                  ),
                )
              }
              placeholder="max"
              value={jauge.max}
            />
            <input
              aria-label={`Départ de la jauge ${rang + 1}`}
              className={CHAMP}
              inputMode="numeric"
              onChange={(evenement) =>
                setJauges((avant) =>
                  avant.map((autre, i) =>
                    i === rang ? { ...autre, depart: evenement.target.value } : autre,
                  ),
                )
              }
              placeholder="départ"
              value={jauge.depart}
            />
            <button
              aria-label={`Retirer la jauge ${rang + 1}`}
              className={cn(BOUTON_CONTOUR, "min-h-9 px-3 py-1")}
              onClick={() => setJauges((avant) => avant.filter((_, i) => i !== rang))}
              type="button"
            >
              Retirer
            </button>
          </div>
        ))}
        <div>
          <button
            className={cn(BOUTON_CONTOUR, "min-h-9 px-3 py-1")}
            disabled={jauges.length >= JAUGES_JEU_MAX}
            onClick={() =>
              setJauges((avant) => [
                ...avant,
                { ligne: nouvelleLigne(), id: "", libelle: "", max: "3", depart: "0" },
              ])
            }
            type="button"
          >
            Ajouter une jauge
          </button>
        </div>
      </section>

      {refus === null ? null : (
        <p className="text-sm text-destructive" role="alert">
          {refus}
        </p>
      )}
      {ecrire.isError ? (
        <div className="flex flex-col gap-2" role="alert">
          <p className="text-sm text-destructive">{messageDEchec(ecrire.error)}</p>
          {ecrire.error instanceof RefusJeu && ecrire.error.code === "version_perimee" ? (
            <div>
              <button
                className={cn(BOUTON_CONTOUR, "min-h-9 px-3 py-1")}
                onClick={() =>
                  void client.invalidateQueries({ queryKey: cleLivre(String(livre.id)) })
                }
                type="button"
              >
                Recharger les réglages
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
      {enregistre ? (
        <p className={ALERTE_VERTE} role="status">
          Réglages enregistrés.
        </p>
      ) : null}
      <p className={ALERTE_GRISE}>
        Un indice ou une jauge retiré ici reste écrit dans les choix des scènes : la vérification le
        signalera.
      </p>
      <div>
        <Button
          aria-disabled={ecrire.isPending || undefined}
          className="aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
          type="submit"
        >
          Enregistrer les réglages
        </Button>
      </div>
    </form>
  );
}
